import { setHasCameraAccess } from '../cameraSlice'
import { setSnackbarMessages } from '../../snackbar/snackbarSlice'
import useAppDispatch from '../../../hooks/useAppDispatch'
import { setQRPaused } from '../../checkin/checkinSlice'
import { createLogger } from '../../../app/logger'
import { useCallback, useState } from 'react'

const logger = createLogger({
  prefix: 'cameraAccess'
})

export enum PermissionStatus {
  // eslint-disable-next-line
  PROMPT = 'prompt',
  // eslint-disable-next-line
  GRANTED = 'granted',
  // eslint-disable-next-line
  DENIED = 'denied',
}

export const MAX_DENIED_CAMERA_ACCESS_COUNT = 3

function isSafari() {
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
}

const useCheckCameraAccess = () => {
  const [accessDeniedCount, setAccessDeniedCount] = useState<number>(0)
  const dispatch = useAppDispatch()

  const handleGrantedAccess = useCallback(() => {
    dispatch(setHasCameraAccess(true))
    dispatch(setQRPaused(false))
  }, [dispatch])

  const handleDeniedAccess = useCallback(() => {
    dispatch(setHasCameraAccess(false))
    dispatch(setQRPaused(true))
    const message = isSafari() && accessDeniedCount >= MAX_DENIED_CAMERA_ACCESS_COUNT
      ? 'Camera access denied 3 times. Please re-open the browser'
      : 'Camera access is not granted'
    dispatch(setSnackbarMessages({
      messageLines: [{ message }],
      severity: 'error'
    }))
    setAccessDeniedCount(prevCount => prevCount + 1)
  }, [dispatch, accessDeniedCount])

  const requestCameraPermission = useCallback(async () => {
    try {
      logger.info('REQUESTING CAMERA PERMISSION')
      const stream = await navigator.mediaDevices.getUserMedia({ video: true })
      stream.getTracks().forEach(track => track.stop())
      handleGrantedAccess()
    } catch (error) {
      handleDeniedAccess()
      logger.error('ACCESS DENIED ON PROMPT', error)
    }
  }, [handleGrantedAccess, handleDeniedAccess])

  return useCallback(async () => {
    if (navigator.permissions) {
      try {
        logger.info('CHECKING CAMERA PERMISSIONS')
        // @ts-ignore
        const permissionStatus = await navigator.permissions.query({ name: 'camera' })
        logger.info('PERMISSION STATUS', permissionStatus)
        switch (permissionStatus.state) {
          case PermissionStatus.PROMPT:
            logger.info('PERMISSION STATE: PROMPT')
            await requestCameraPermission()
            break
          case PermissionStatus.GRANTED:
            logger.info('PERMISSION STATE: GRANTED')
            handleGrantedAccess()
            break
          case PermissionStatus.DENIED:
            logger.error('PERMISSION STATE: DENIED')
            handleDeniedAccess()
            break
          default:
            logger.error('UNKNOWN PERMISSION STATE', permissionStatus.state)
        }
      } catch (err) {
        logger.error('ERROR WHILE QUERYING CAMERA PERMISSIONS:', err)
      }
    } else {
      logger.warn('NAVIGATOR.PERMISSIONS API IS NOT AVAILABLE. REQUESTING CAMERA PERMISSION DIRECTLY.')
      await requestCameraPermission()
    }
  }, [
    handleGrantedAccess,
    handleDeniedAccess,
    requestCameraPermission
  ])
}

export default useCheckCameraAccess
